import { useEffect, useRef, useState } from 'react';

import groupAddImage from '@assets/images/group-add.svg';
import { Icon, ImageUpload, Input } from '@components/index';
import { NAME_MAX_LENGTH, NAME_MIN_LENGTH } from '@constants/generic';
import { groupNameRegex } from '@constants/regex';
import { ImageForm } from '@types';
import {
  getNameErrorMessage,
  getNameErrorMessageMax,
  isMaxLengthSatisfied,
  isMinLengthSatisfied
} from '@utils/generic';

interface GroupBasicDetailsProps {
  formData: {
    groupPhoto: any;
    groupName: string;
  };
  isEditGroup?: boolean;
  onInputChange: (field: string, value: any) => void;
}

const GroupBasicDetails = ({
  formData,
  isEditGroup = false,
  onInputChange
}: GroupBasicDetailsProps) => {
  const [isEditing, setIsEditing] = useState(
    !formData.groupName && !isEditGroup
  );
  const [nameError, setNameError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const onGroupNameChange = (value: string) => {
    setNameError('');
    onInputChange('groupName', value);
  };

  const onGroupNameBlur = (value: string) => {
    if (!isMinLengthSatisfied(value)) {
      setNameError(getNameErrorMessage());
    } else if (!isMaxLengthSatisfied(value)) {
      setNameError(getNameErrorMessageMax());
    } else {
      setIsEditing(false);
    }
  };

  const onPhotoChange = (image: ImageForm) => {
    onInputChange('groupPhoto', image);
  };

  const onClearPhoto = () => {
    onInputChange('groupPhoto', {});
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  useEffect(() => {
    if (formData.groupName.includes('Copy')) {
      if (!isMinLengthSatisfied(formData.groupName)) {
        setNameError(getNameErrorMessage());
        setIsEditing(true);
      } else if (!isMaxLengthSatisfied(formData.groupName)) {
        setNameError(getNameErrorMessageMax());
        setIsEditing(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.groupName]);

  const imageSrc =
    formData?.groupPhoto?.imageUrl || formData?.groupPhoto?.preview;

  return (
    <div className="flex items-center gap-6 rounded-xl border border-cofeeGreyBlue200 bg-cofeeNeutral1 p-6">
      <div className="relative shrink-0">
        <ImageUpload
          label="Group Photo"
          hideLabel
          imageSrc={imageSrc}
          showDeleteIcon={!!imageSrc}
          onClearImage={onClearPhoto}
          onImageChange={onPhotoChange}
          placeholderImage={groupAddImage}
          placeholderClassName="size-20"
          imgClassName="!size-20"
        />
      </div>
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {isEditing ? (
          <div className="w-full max-w-80">
            <Input
              ref={inputRef}
              name="groupName"
              value={formData.groupName}
              placeholder="Eg: Dance Class A"
              handleInputChange={onGroupNameChange}
              inputRegex={groupNameRegex}
              maxLength={NAME_MAX_LENGTH}
              minLength={NAME_MIN_LENGTH}
              error={!!nameError}
              errorMessage={nameError}
              handleInputBlur={onGroupNameBlur}
              className="rounded-lg"
              inputStyle="!bg-white"
            />
          </div>
        ) : (
          <>
            <h2 className="truncate text-2xl font-semibold text-cofeeNeutral10">
              {formData.groupName || 'Untitled Group'}
            </h2>
            <Icon
              name="edit-pencil"
              size="16"
              className="ml-3 shrink-0 cursor-pointer"
              onClick={handleEditClick}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default GroupBasicDetails;
